import { m, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import type { ReactNode } from "react";

/**
 * Full-bleed hero with a gently parallaxing background image layer + a
 * gradient overlay layer. The image drifts ~12% and eases out of a slight
 * zoom as the hero scrolls away. Transform-only (compositor) so it stays
 * smooth even with large images. Static when reduced-motion is requested.
 *
 * Requires CSS for `.hero-parallax__bg` (absolute, inset:-6%, cover) and
 * `.hero-parallax__overlay` (absolute, inset:0). Hero content should sit in a
 * positioned wrapper with z-index above these layers.
 *
 * Art direction: the plate is handed to CSS as the custom property
 * `--hero-image` rather than an inline `background-image`, so a media query can
 * swap it per breakpoint (an inline background-image could only be overridden
 * with `!important`). Pass `imageWide` to supply a separate desktop plate —
 * heritage.css picks it up from 1021px, where the hero turns landscape and a
 * wide establishing shot reads as too distant.
 */
export function HeroParallax({
  image,
  imageWide,
  overlay,
  className,
  children,
}: {
  image: string;
  imageWide?: string;
  overlay: string;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.06, 1]);
  const plates = {
    "--hero-image": `url(${image})`,
    ...(imageWide ? { "--hero-image-wide": `url(${imageWide})` } : {}),
  } as Record<string, string>;

  return (
    <section ref={ref} className={className} style={{ position: "relative", overflow: "hidden" }}>
      <m.div
        aria-hidden
        className="hero-parallax__bg"
        style={reduce ? plates : { ...plates, y, scale }}
      />
      <div aria-hidden className="hero-parallax__overlay" style={{ background: overlay }} />
      {children}
    </section>
  );
}
